import type {
  CogniteClient,
  NodeDefinition,
  PropertySortV3,
  PropertyValueGroupV3,
  RawPropertyValueV3,
  ViewDefinition,
  ViewReference,
} from '@cognite/sdk';

import { getCdmViewReferencesFromModelVersion } from '../../../providers/fdm-services/cdm-view-reference-utils';
import { toViewReference } from '../../app-config/utils/cdm-utils';
import type { IViewService } from '../../view/services/view-service.interface';
import {
  DEFAULT_OBSERVATIONS_LIMIT,
  INFIELD_IDM_OBSERVATIONS_SOURCE_SYSTEM_NODE_PROPERTIES_V_1,
  INFIELD_SOURCE_SYSTEM_EXTERNAL_ID_V_1,
  INFIELD_SOURCE_SYSTEM_VIEW,
} from '../consts';
import type {
  NodeRef,
  ObservationViewProperty,
  PotentiallyExtendedCogniteObservation,
  PropertyRecord,
  SimplifiedSortOption,
} from '../types';

import type {
  IdmObservationViewConfig,
  IIdmObservationsService,
} from './idm-observations-service.interface';
import {
  buildDirectRelationMap,
  buildQuery,
  enrichNodesWithRelations,
} from './query-builder';
import {
  findViewPropertyByContainer,
  remapRawNodeDefinitionToObservationType,
  remapViewDefinitionPropertyToObservationProperty,
} from './utils';

export interface CogniteClientDependencies {
  project: CogniteClient['project'];
  instances: {
    list: CogniteClient['instances']['list'];
    upsert: CogniteClient['instances']['upsert'];
    query: CogniteClient['instances']['query'];
    delete: CogniteClient['instances']['delete'];
  };
  getBaseUrl: CogniteClient['getBaseUrl'];
  post: CogniteClient['post'];
}

export type UpsertNodeInstanceProps = {
  externalId: string;
  space: string;
  viewReference: ViewReference;
  viewDefinition: ViewDefinition;
  propertyRecord: PropertyRecord;
  userId?: string;
};

type GetObservationsByViewOptions = {
  sort?: SimplifiedSortOption;
  spaces?: string[];
  limit?: number;
  cursor?: string;
};

export class IdmObservationsService implements IIdmObservationsService {
  private cogniteClient: CogniteClientDependencies;
  private viewService: IViewService;
  readonly viewReference: ViewReference;
  readonly fieldsConfig?: IdmObservationViewConfig['fieldsConfig'];

  constructor(
    client: CogniteClientDependencies,
    viewService: IViewService,
    viewConfig?: IdmObservationViewConfig
  ) {
    this.cogniteClient = client;
    const observationViewReference = viewConfig?.view
      ? toViewReference(viewConfig.view)
      : getCdmViewReferencesFromModelVersion().infieldFieldObservation;

    this.viewReference = observationViewReference;
    this.fieldsConfig = viewConfig?.fieldsConfig;
    this.viewService = viewService;
  }

  private async retrieveObservationNodes(
    viewReference: ViewReference,
    limit: number,
    sort?: SimplifiedSortOption,
    spaces: string[] = [],
    cursor?: string
  ): Promise<{ nodes: NodeDefinition[]; nextCursor?: string }> {
    const viewDefinition =
      await this.viewService.fetchViewDefinition(viewReference);

    const spaceFilter =
      spaces.length > 0
        ? {
            in: {
              property: ['node', 'space'],
              values: spaces,
            },
          }
        : undefined;

    const defaultSort: PropertySortV3[] | undefined = sort
      ? [
          {
            property: [
              viewReference.space,
              `${viewReference.externalId}/${viewReference.version}`,
              sort.propertyId,
            ],
            direction: sort.sortDirection,
            nullsFirst: true,
          },
        ]
      : undefined;

    const { query, baseNodeKey, relationKeys, directRelationProperties } =
      buildQuery(
        viewReference,
        viewDefinition,
        defaultSort,
        spaceFilter,
        limit,
        cursor
      );

    const queryResults = await this.cogniteClient.instances.query(query);

    /**
     * Filter to get only the nodes from the main observation query
     */
    const baseInstances = queryResults.items[baseNodeKey] || [];
    const baseNodes = baseInstances.filter(
      (item) => item.instanceType === 'node'
    );

    const directRelationMap = buildDirectRelationMap(
      queryResults,
      relationKeys
    );

    const nodesWithRelations = enrichNodesWithRelations(
      baseNodes,
      directRelationMap,
      directRelationProperties,
      viewReference,
      baseNodeKey
    );

    const nextCursor = queryResults.nextCursor?.[baseNodeKey];

    return {
      nodes: nodesWithRelations,
      nextCursor,
    };
  }

  /**
   * @param space - The space where the node instance will be stored (can differ from the view definition space).
   * @returns A promise that resolves with an object containing a reference to the created node.
   */
  private async createInfieldSourceSystem(
    space: string
  ): Promise<{ externalId: string; space: string } | undefined> {
    const upsertedInstance = await this.cogniteClient.instances.upsert({
      items: [
        {
          instanceType: 'node',
          externalId: INFIELD_SOURCE_SYSTEM_EXTERNAL_ID_V_1,
          space,
          // Setting existingVersion to 0 makes this behave like an insert-only operation.
          // If a node with this externalId already exists and if skipOnVersionConflict is true,
          // the operation will skip this item
          existingVersion: 0,
          sources: [
            {
              source: INFIELD_SOURCE_SYSTEM_VIEW,
              properties:
                INFIELD_IDM_OBSERVATIONS_SOURCE_SYSTEM_NODE_PROPERTIES_V_1,
            },
          ],
        },
      ],
      // If we try to create the source system when it already exists,
      // skip this item instead of failing the entire request.
      // This works in conjunction with existingVersion: 0.
      skipOnVersionConflict: true,
    });

    const upsertedNode = upsertedInstance.items?.[0];

    return upsertedNode
      ? {
          externalId: upsertedNode.externalId,
          space: upsertedNode.space,
        }
      : undefined;
  }

  /**
   * Fetches the properties of a view based on the provided view reference.
   * @param viewReference - The reference to the view whose properties are to be fetched.
   * @returns An array of observation view properties.
   */
  public async getViewProperties(
    viewReference: ViewReference
  ): Promise<ObservationViewProperty[]> {
    const viewDefinition =
      await this.viewService.fetchViewDefinition(viewReference);
    if (!viewDefinition) {
      return [];
    }

    return Object.entries(viewDefinition.properties).map(([key, value]) =>
      remapViewDefinitionPropertyToObservationProperty(key, value)
    );
  }

  /**
   * Fetches observations based on the provided view reference.
   * @param viewReference - The reference to the view whose observations are to be fetched.
   * @param options - Optional configuration object containing sort, spaces, limit, and cursor
   * @returns Object containing observations and next cursor for pagination
   */
  public async getObservationsByView(
    viewReference: ViewReference,
    options: GetObservationsByViewOptions = {}
  ): Promise<{
    observations: PotentiallyExtendedCogniteObservation[];
    nextCursor?: string;
  }> {
    const {
      sort,
      spaces = [],
      limit = DEFAULT_OBSERVATIONS_LIMIT,
      cursor,
    } = options;

    const { nodes: observationNodes, nextCursor } =
      await this.retrieveObservationNodes(
        viewReference,
        limit,
        sort,
        spaces,
        cursor
      );

    const typedObservations = observationNodes.map((node) =>
      remapRawNodeDefinitionToObservationType(node, viewReference)
    );

    return {
      observations: typedObservations,
      nextCursor,
    };
  }

  /**
   * Creates a new instance of a specified view type based on the input values
   *
   * This method:
   * - Formats the given property record into a structure suitable for upsert.
   * - Automatically fills out supported `CogniteSourceable` properties if they exist in the view definition.
   * - upsert the provided instance.
   *
   * @param externalId - The identifier of the node instance.
   * @param space - The space where the node instance will be stored (can differ from the view definition space).
   * @param viewReference - A reference to the view type through which the properties are ingested.
   * @param viewDefinition - The view definition containing metadata and property structure.
   * @param propertyRecord - The user-provided property values for the node.
   * @returns A promise that resolves with an object containing a reference to the created node.
   */
  public async upsertNodeInstance({
    externalId,
    space,
    viewReference,
    viewDefinition,
    propertyRecord,
    userId,
  }: UpsertNodeInstanceProps): Promise<{ externalId: string; space: string }> {
    const formattedPropertyValues = propertyRecord as PropertyValueGroupV3;

    /**
     * NOTE(eiriklv): Find any relevant CogniteSourceable
     * properties that exist in the view definition, so
     * that we can fill them out accordingly
     */
    const cogniteSourceableSourceCreatedTime = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceCreatedTime'
    );

    const cogniteSourceableSourceUpdatedTime = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceUpdatedTime'
    );

    /**
     * NOTE(eiriklv): Add custom handling for any existing
     * CogniteSourceable properties that are part of the
     * view definition. Fill out only the ones that exist,
     * but always fill them out automatically (they will always be hidden in the form)
     *
     * TODO(FO-1307): Add handling for the remaining CogniteSourceable
     * properties that are not yet covered
     */

    const cogniteSourceableSourceCreatedUser = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceCreatedUser'
    );

    const cogniteSourceableSourceUpdatedUser = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceUpdatedUser'
    );

    const cogniteSourceableSource = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'source'
    );

    let sourcePropDirectRelation: RawPropertyValueV3 | undefined =
      cogniteSourceableSource
        ? formattedPropertyValues[cogniteSourceableSource.id]
        : undefined;

    // If the source property exists but no value is provided,
    // create an "Infield as CogniteSourceSystem" node to use as the source link.
    if (cogniteSourceableSource && !sourcePropDirectRelation) {
      sourcePropDirectRelation = await this.createInfieldSourceSystem(space);
    }

    const sourcableProperties: PropertyValueGroupV3 = ((
      now = new Date().toJSON()
    ) => ({
      ...(cogniteSourceableSourceCreatedTime
        ? {
            [cogniteSourceableSourceCreatedTime.id]:
              formattedPropertyValues[cogniteSourceableSourceCreatedTime.id] ??
              now,
          }
        : {}),
      ...(cogniteSourceableSourceUpdatedTime
        ? {
            [cogniteSourceableSourceUpdatedTime.id]: now,
          }
        : {}),
      ...(cogniteSourceableSource && sourcePropDirectRelation
        ? {
            [cogniteSourceableSource.id]: sourcePropDirectRelation,
          }
        : {}),
      ...(cogniteSourceableSourceCreatedUser
        ? {
            [cogniteSourceableSourceCreatedUser.id]:
              formattedPropertyValues[cogniteSourceableSourceCreatedUser.id] ??
              userId,
          }
        : {}),
      ...(cogniteSourceableSourceUpdatedUser && userId
        ? {
            [cogniteSourceableSourceUpdatedUser.id]: userId,
          }
        : {}),
    }))();

    /**
     * Perform the upsert
     */
    await this.cogniteClient.instances.upsert({
      items: [
        {
          instanceType: 'node',
          externalId,
          /**
           * NOTE(eirikv): This is the storage space for the instance,
           * which will be different from the space where the view definition is stored
           */
          space,
          sources: [
            {
              /**
               * NOTE(eiriklv): Here is where we specify
               * which view to ingest these properties through
               */
              source: viewReference,
              properties: {
                /**
                 * NOTE(eiriklv): Add the user filled properties
                 */
                ...formattedPropertyValues,
                /**
                 * NOTE(eiriklv): Add application filled CogniteSourcable properties
                 */
                ...sourcableProperties,
              },
            },
          ],
        },
      ],
    });

    /**
     * Return a reference to the created node
     *
     * TODO(eiriklv): Should anything else be returned here..?
     */
    return { externalId, space };
  }
  /**
   * Deletes multiple node instances.
   * @param nodes - An array of nodes to delete, each with a space and externalId.
   */
  public async deleteNodeInstance(nodes: NodeRef[]): Promise<NodeRef[]> {
    if (!nodes.length) return [];

    await this.cogniteClient.instances.delete(
      nodes.map(({ space, externalId }) => ({
        instanceType: 'node',
        space,
        externalId,
      }))
    );
    return nodes;
  }
}

export const createIdmObservationsService = (params: {
  client: CogniteClientDependencies;
  viewService: IViewService;
  viewConfig?: IdmObservationViewConfig;
  /** @deprecated pass viewConfig instead; kept until callers migrate off viewReference */
  viewReference?: ViewReference;
}): IIdmObservationsService =>
  new IdmObservationsService(
    params.client,
    params.viewService,
    params.viewConfig ??
      (params.viewReference ? { view: params.viewReference } : undefined)
  );
